import { Check, DoNotDisturb, Save } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { Control, FieldValues, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { GqlObservationStatus } from "~/__generated__";
import i18n from "~/config/i18n";

interface ObservationActionsBoxProps<T extends FieldValues = FieldValues> {
    control: Control<T>;
    onCancelClick: () => void;
    onChangeStatusClick: (nextValue: GqlObservationStatus) => void;
    onSubmitClick: () => void;
    status: GqlObservationStatus;
}

export function ObservationActionsBox<T extends FieldValues = FieldValues>({
    control,
    onCancelClick,
    onChangeStatusClick,
    onSubmitClick,
    status,
}: ObservationActionsBoxProps<T>) {
    const { isDirty, isLoading } = useFormState({ control });

    return (
        <Stack
            direction="row"
            p={2}
            gap={1}
            justifyContent="space-between"
            alignItems="center"
            sx={theme => ({ borderTop: `1px solid ${theme.palette.divider}` })}
        >
            {!isDirty ? (
                <ChangeStatusButtons onChangeStatusClick={onChangeStatusClick} status={status} />
            ) : (
                <SubmitOrCancelButtons onCancelClick={onCancelClick} onSubmitClick={onSubmitClick} isLoading={isLoading} />
            )}
        </Stack>
    );
}

interface ChangeStatusButtonsProps {
    onChangeStatusClick: (nextValue: GqlObservationStatus) => void;
    status: GqlObservationStatus;
}

function ChangeStatusButtons({ onChangeStatusClick, status }: ChangeStatusButtonsProps) {
    const { t } = useTranslation("mapEditor");
    const transitions = ALLOWED_TRANSITIONS[status] ?? [];

    if (transitions.length < 1) {
        return (
            <Button disabled size="small" sx={{ p: 2 }}>
                {t("featureDetailObservation.btnDisabledIncomplete")}
            </Button>
        );
    }

    return transitions.map(transition => {
        const settings = TRANSITION_SETTINGS[transition];

        return (
            <Button
                key={transition}
                color={settings.color}
                startIcon={settings.icon}
                onClick={() => onChangeStatusClick(transition)}
                variant="contained"
            >
                {settings.label}
            </Button>
        );
    });
}

interface SubmitOrCancelButtonsProps {
    onCancelClick: () => void;
    onSubmitClick: () => void;
    isLoading: boolean;
}

function SubmitOrCancelButtons({ onCancelClick, onSubmitClick, isLoading }: SubmitOrCancelButtonsProps) {
    const { t } = useTranslation("mapEditor");

    return (
        <>
            <Button size="small" color="inherit" startIcon={<DoNotDisturb />} onClick={onCancelClick}>
                {t("featureDetailObservation.btnCancel")}
            </Button>
            <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<Save />}
                loading={isLoading}
                onClick={onSubmitClick}
            >
                {t("featureDetailObservation.btnSave")}
            </Button>
        </>
    );
}

const ALLOWED_TRANSITIONS = {
    [GqlObservationStatus.AwaitingApproval]: [GqlObservationStatus.Approved, GqlObservationStatus.Rejected],
    [GqlObservationStatus.Approved]: [GqlObservationStatus.Rejected],
    [GqlObservationStatus.Rejected]: [GqlObservationStatus.Approved],
    [GqlObservationStatus.Deleted]: [],
    [GqlObservationStatus.Incomplete]: [],
} as const;

const TRANSITION_SETTINGS = {
    [GqlObservationStatus.Approved]: {
        color: "primary",
        label: i18n.t("featureDetailObservation.btnApprove", { ns: "mapEditor" }),
        icon: <Check />,
    },
    [GqlObservationStatus.Rejected]: {
        color: "error",
        label: i18n.t("featureDetailObservation.btnReject", { ns: "mapEditor" }),
        icon: <DoNotDisturb />,
    },
} as const;
